import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { find, findIndex } from "lodash";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

interface RemoveRouteLocationButtonProps {
  order: number;
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
}

const RemoveRouteLocationButton: React.FC<RemoveRouteLocationButtonProps> = ({
  order,
  school,
  busSchedule,
  maxStopTime,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);

  const routeLocationsField = useField("routeLocations");
  const routeGeometriesField = useField(`routeGeometries`);

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    const findRouteLocation = find(routeLocationsField.input.value, [
      "order",
      order,
    ]);
    setSelectedRouteLocation(findRouteLocation);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveRemoveRouteLocation = async () => {
    try {
      setIsLoading(true);

      const findRouteLocationIndex = findIndex(
        routeLocationsField.input.value,
        ["order", selectedRouteLocation.order],
      );

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        [
          ...routeLocationsField.input.value.slice(0, findRouteLocationIndex),
          ...routeLocationsField.input.value.slice(findRouteLocationIndex + 1),
        ],
        busSchedule.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        school,
        busSchedule,
        maxStopTime,
        routeLocationWithoutSchool,
      );

      routeLocationsField.input.onChange(optimisedRouteLocations);
      routeGeometriesField.input.onChange([]);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      notify("Failed to remove route location", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <StyledButton variant="outlined" onClick={handleOpenDialog}>
        Remove
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Remove {selectedRouteLocation?.address}
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Are you sure you want to remove this location?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveRemoveRouteLocation}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default RemoveRouteLocationButton;
